import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { STORE_CONTEXT_REFRESH_EVENT } from "@/lib/storeOnboarding";
import { useAuth } from "@/hooks/useAuth";

export type Store = { id: string; name: string };

type DateRange = "7d" | "30d" | "90d" | "today" | "yesterday";

// F-13: `null` = no store chosen yet (or user has 0 authorized stores).
// Consumers MUST treat `null` as "do not run any store-scoped query".
// `"all"` is ONLY valid for users with explicit aggregate access
// (canSelectAllStores === true). Store-scoped queries must additionally
// gate on `storesReady`.
export type StoreFilterId = string | "all" | null;

type AppFilters = {
  storeId: StoreFilterId;
  setStoreId: (id: StoreFilterId) => void;
  dateRange: DateRange;
  setDateRange: (r: DateRange) => void;
  stores: Store[];
  storesLoading: boolean;
  /** True only when the stores list for the CURRENT authenticated user has been loaded. */
  storesReady: boolean;
  storesError: string | null;
  /** True only when the current user has explicit aggregate (global admin/manager) access. */
  canSelectAllStores: boolean;
  refreshKey: number;
  refresh: () => void;
  lastUpdated: Date;
};

const Ctx = createContext<AppFilters | null>(null);

const persistKey = (uid: string) => `storeledger:selected-store:${uid}`;

const readPersisted = (uid: string): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(persistKey(uid));
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
};

const writePersisted = (uid: string, value: string | null) => {
  if (typeof window === "undefined") return;
  try {
    if (value === null) window.localStorage.removeItem(persistKey(uid));
    else window.localStorage.setItem(persistKey(uid), value);
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

export function AppFiltersProvider({ children }: { children: ReactNode }) {
  const { user, roles, loading: authLoading, authorizationUserId } = useAuth();
  const activeUserId = user?.id ?? null;

  // Only trust roles when authorization has been resolved for THIS user.
  const rolesResolved = authorizationUserId === activeUserId && activeUserId !== null;
  const canSelectAllStores = rolesResolved && (roles.includes("admin") || roles.includes("manager"));

  const [storeId, setStoreIdRaw] = useState<StoreFilterId>(null);
  const [dateRange, setDateRange] = useState<DateRange>("30d");
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [stores, setStores] = useState<Store[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesReady, setStoresReady] = useState(false);
  const [storesUserId, setStoresUserId] = useState<string | null>(null);
  const [storesError, setStoresError] = useState<string | null>(null);

  // Generation guard: each user-change or refresh bumps this. Any
  // in-flight fetch that does not match the current generation when it
  // resolves is discarded, so a slow User-A response can never populate
  // state after User B has signed in.
  const generationRef = useRef(0);

  // Synchronously wipe every scrap of the previous user's store state
  // the instant the authenticated user id changes. Bumps the generation
  // so any in-flight fetch is invalidated before it can land.
  useEffect(() => {
    generationRef.current += 1;
    setStores([]);
    setStoresReady(false);
    setStoresError(null);
    setStoreIdRaw(null);
    setStoresUserId(null);
    if (activeUserId === null) {
      setStoresLoading(false);
    }
  }, [activeUserId]);

  // Fetch the authorized store list for the CURRENT user only. Gated on
  // authorization having resolved so we never pick a default from a role
  // set that still belongs to the prior user.
  useEffect(() => {
    if (!activeUserId || authLoading || !rolesResolved) return;
    const myGeneration = ++generationRef.current;
    const myUserId = activeUserId;
    const myCanSelectAll = canSelectAllStores;
    let cancelled = false;

    setStoresLoading(true);
    setStoresError(null);

    (async () => {
      const { data, error } = await supabase
        .from("stores")
        .select("id, name")
        .eq("is_active", true)
        .order("name", { ascending: true });
      if (cancelled) return;
      if (myGeneration !== generationRef.current) return; // stale user/refresh
      if (myUserId !== activeUserId) return; // user changed mid-flight

      if (error) {
        setStoresError(error.message);
        setStores([]);
        setStoreIdRaw(null);
        setStoresReady(true);
        setStoresLoading(false);
        setStoresUserId(myUserId);
        return;
      }

      const list = (data ?? []) as Store[];
      setStores(list);
      setStoresUserId(myUserId);

      // Safe selection rules (F-13 requirement 3):
      let next: StoreFilterId = null;
      if (list.length === 0) {
        next = null;
      } else if (list.length === 1) {
        next = list[0].id;
      } else {
        const persisted = readPersisted(myUserId);
        if (persisted && list.some((s) => s.id === persisted)) {
          next = persisted;
        } else if (persisted === "all" && myCanSelectAll) {
          next = "all";
        } else {
          next = null; // require explicit choice for multi-store users
        }
      }
      setStoreIdRaw(next);
      setStoresReady(true);
      setStoresLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [activeUserId, authLoading, rolesResolved, canSelectAllStores, refreshKey]);

  // Public setter validates the choice against the current user's
  // authorized list and aggregate permission before persisting.
  const setStoreId = (id: StoreFilterId) => {
    if (id === "all") {
      if (!canSelectAllStores) return; // reject unauthorized aggregate selection
    } else if (id !== null) {
      if (!stores.some((s) => s.id === id)) return; // reject unknown store
    }
    setStoreIdRaw(id);
    if (activeUserId) {
      writePersisted(activeUserId, id === null ? null : id);
    }
  };

  useEffect(() => {
    const refreshStores = () => {
      setRefreshKey((key) => key + 1);
      setLastUpdated(new Date());
    };
    window.addEventListener(STORE_CONTEXT_REFRESH_EVENT, refreshStores);
    return () => window.removeEventListener(STORE_CONTEXT_REFRESH_EVENT, refreshStores);
  }, []);

  // If the selected id disappears from the freshly loaded list, drop it.
  // Never silently fall back to "all" -- that would leak aggregate view
  // to a store-scoped user (F-13 requirement 4).
  useEffect(() => {
    if (!storesReady) return;
    if (storeId === null) return;
    if (storeId === "all") {
      if (!canSelectAllStores) setStoreIdRaw(null);
      return;
    }
    if (!stores.some((s) => s.id === storeId)) setStoreIdRaw(null);
  }, [storeId, stores, storesReady, canSelectAllStores]);

  const value = useMemo<AppFilters>(() => ({
    storeId,
    setStoreId,
    dateRange,
    setDateRange,
    stores,
    storesLoading,
    storesReady: storesReady && storesUserId === activeUserId,
    storesError,
    canSelectAllStores,
    refreshKey,
    refresh: () => { setRefreshKey((k) => k + 1); setLastUpdated(new Date()); },
    lastUpdated,
  }), [storeId, dateRange, stores, storesLoading, storesReady, storesUserId, activeUserId, storesError, canSelectAllStores, refreshKey, lastUpdated]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAppFilters() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAppFilters must be used within AppFiltersProvider");
  return v;
}

export const dateRangeDays = (r: DateRange) => {
  switch (r) {
    case "today": return 1;
    case "yesterday": return 1;
    case "7d": return 7;
    case "30d": return 30;
    case "90d": return 90;
  }
};

export const dateRangeLabel = (r: DateRange) => ({
  today: "Today",
  yesterday: "Yesterday",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
} as const)[r];
